import { useCallback, useEffect, useRef, useState } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { exportSreFindings } from '../../../../../AGSS/api/AGRMApi';
import {
    SreEntityType,
    SreInflightSearchQuery,
} from '../../../../../AGSS/models/SreHealth';
import {
    buildFindingsCsv,
    buildUniqueFindingsCsv,
    downloadCsv,
    findingsCsvFileName,
} from './csvExport';
import { findingEntries } from './findingFormat';
import { sreCacheScope } from './sreSessionCache';

type ExportMode = 'findings' | 'uniqueEntries';

export const useFindingsExport = (
    type: SreEntityType,
    query: SreInflightSearchQuery
) => {
    const signature = JSON.stringify(query);
    const scope = sreCacheScope();
    const active = useRef<CancelTokenSource | null>(null);
    const continuation = useRef<string | null>(null);
    const part = useRef(0);
    const exportMode = useRef<ExportMode>('findings');
    const seenEntries = useRef(new Set<string>());
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        continuation.current = null;
        part.current = 0;
        seenEntries.current.clear();
        setHasMore(false);
        setBusy(false);
        setMessage(null);
        return () => {
            active.current?.cancel('Export context changed');
            active.current = null;
            seenEntries.current.clear();
        };
    }, [signature, type, scope]);

    const run = useCallback(
        async (resume = false, mode: ExportMode = 'findings') => {
            if (active.current) return;
            const source = axios.CancelToken.source();
            active.current = source;
            setBusy(true);
            setMessage(null);
            if (!resume) {
                continuation.current = null;
                part.current = 0;
                exportMode.current = mode;
                seenEntries.current.clear();
                setHasMore(false);
            }
            try {
                const result = await exportSreFindings(
                    type,
                    JSON.parse(signature),
                    source.token,
                    continuation.current
                );
                if (active.current !== source) return;
                const uniqueOnly = exportMode.current === 'uniqueEntries';
                const entries = uniqueOnly
                    ? findingEntries(result.rows).filter(
                          (entry) => !seenEntries.current.has(entry)
                      )
                    : [];
                const count = uniqueOnly ? entries.length : result.rows.length;
                const unit = uniqueOnly ? 'unique findings' : 'findings';
                if (count) {
                    const nextPart = part.current + 1;
                    const fileName = findingsCsvFileName(type)
                        .replace(
                            'sre-findings-',
                            uniqueOnly
                                ? 'sre-unique-findings-'
                                : 'sre-findings-'
                        )
                        .replace('.csv', `-part-${nextPart}.csv`);
                    downloadCsv(
                        fileName,
                        uniqueOnly
                            ? buildUniqueFindingsCsv(entries)
                            : buildFindingsCsv(result.rows)
                    );
                    entries.forEach((entry) => seenEntries.current.add(entry));
                    part.current = nextPart;
                }
                continuation.current = result.nextContinuationToken ?? null;
                setHasMore(Boolean(continuation.current));
                const suffix = continuation.current
                    ? ' More results are available.'
                    : result.truncated
                    ? ' This SRE version capped the export; narrow the filter for the remaining findings.'
                    : ' Export complete.';
                setMessage(
                    count
                        ? `Exported ${count.toLocaleString()} ${unit} in part ${
                              part.current
                          }.${suffix}`
                        : `No new ${unit} in this batch.${suffix}`
                );
                if (!continuation.current) seenEntries.current.clear();
            } catch (error) {
                if (active.current === source && !axios.isCancel(error))
                    setMessage(
                        (error as any)?.response?.data?.message ??
                            (error as Error)?.message ??
                            'Findings export failed.'
                    );
            } finally {
                if (active.current === source) {
                    active.current = null;
                    setBusy(false);
                }
            }
        },
        [signature, type, scope]
    );

    const cancel = () => {
        active.current?.cancel('Export cancelled');
        active.current = null;
        setBusy(false);
        setMessage('Export cancelled.');
    };
    return {
        busy,
        message,
        hasMore,
        run,
        cancel,
        dismiss: () => setMessage(null),
    };
};
